import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowsClockwise, XCircle, Circle, Play, Pause, FolderSimple, CaretRight, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStudent } from "@/context/StudentContext";
import { PageHeader } from "@/components/PageHeader";
import { TahfidzPanel } from "@/components/TahfidzPanel";
import { ReactionThread } from "@/components/ReactionThread";

const CATS = [
  ["tahfidz", "Tahfidz"], ["doa", "Doa"], ["hadits", "Hadits"],
  ["arab", "B. Arab"], ["inggris", "B. Inggris"], ["lainnya", "Lainnya"],
];

export const STATUS_META = {
  lancar: { label: "Lancar", cls: "bg-green-100 text-green-700", icon: CheckCircle, dot: "text-green-600" },
  ulang: { label: "Belum Lancar", cls: "bg-yellow-100 text-yellow-700", icon: ArrowsClockwise, dot: "text-yellow-600" },
  belum: { label: "Belum Hafal", cls: "bg-red-100 text-red-700", icon: XCircle, dot: "text-red-600" },
};

export default function Hafalan() {
  const { user } = useAuth();
  const { active } = useStudent();
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState(null);
  const [tab, setTab] = useState("tahfidz");
  const [catalog, setCatalog] = useState({ tahfidz: [], doa: [], hadits: [], arab: [], inggris: [], lainnya: [] });
  const [progress, setProgress] = useState([]);
  const [folders, setFolders] = useState([]);
  const [path, setPath] = useState([]);
  const folder = path[path.length - 1] || null;
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); };
  const toggle = (it) => {
    if (playing === it.key) { stop(); return; }
    stop(); const a = new Audio(it.audio); a.onended = () => setPlaying(null); a.play().catch(() => {}); audioRef.current = a; setPlaying(it.key);
  };

  useEffect(() => {
    api.get("/progress/catalog").then((r) => setCatalog(r.data));
    if (user?.role !== "orangtua") api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [user]);
  useEffect(() => { if (user?.role === "orangtua") setSid(active?.id || null); }, [user, active]);
  useEffect(() => { if (sid) api.get(`/progress?student_id=${sid}`).then((r) => setProgress(r.data)); }, [sid]);
  useEffect(() => { stop(); setPath([]); if (tab !== "tahfidz") api.get(`/content-folders/${tab}`).then((r) => setFolders(r.data)); }, [tab]);

  const byKey = {};
  progress.filter((p) => p.category === tab).forEach((p) => { byKey[p.item_key] = p; });
  const allItems = tab === "tahfidz" ? [] : (catalog[tab] || []).map((c) => ({ ...c, status: byKey[c.key]?.status, note: byKey[c.key]?.note, thread: byKey[c.key]?.thread || [] }));
  const currentFid = folder?.id || null;
  const subFolders = folders.filter((f) => (f.parent_id || null) === currentFid);
  const items = allItems.filter((it) => (it.folder_id || null) === currentFid);

  return (
    <div>
      <PageHeader title="Hafalan & Capaian" />
      <div className="px-5 pt-4">
        {user?.role !== "orangtua" && (
          <select data-testid="hafalan-student-select" value={sid || ""} onChange={(e) => setSid(e.target.value)}
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary">
            {students.map((s) => <option key={s.id} value={s.id}>{`${s.name} — ${s.kelas}`}</option>)}
          </select>
        )}

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {CATS.map(([v, l]) => (
            <button key={v} data-testid={`haf-tab-${v}`} onClick={() => setTab(v)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>{l}</button>
          ))}
        </div>

        <div className="flex gap-3 mb-4 text-[11px] flex-wrap">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className="flex items-center gap-1 text-stone-500"><m.icon size={14} weight="fill" className={m.dot} /> {m.label}</span>
          ))}
        </div>

        {!sid ? <p className="text-center text-stone-400 mt-10">Belum ada data ananda.</p> : tab === "tahfidz" ? (
          <TahfidzPanel studentId={sid} mode="view" />
        ) : (
          <div className="space-y-3">
            {path.length > 0 && (
              <button data-testid="haf-folder-back" onClick={() => setPath(path.slice(0, -1))} className="flex items-center gap-1 text-primary text-sm font-semibold"><ArrowLeft size={16} weight="bold" /> Kembali</button>
            )}
            {folder && <h2 className="font-heading font-bold text-stone-800 flex items-center gap-2"><FolderSimple size={18} weight="fill" className="text-amber-500" /> {folder.title}</h2>}
            {subFolders.map((f) => (
              <button key={f.id} data-testid={`haf-folder-${f.id}`} onClick={() => setPath([...path, f])}
                className="w-full bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><FolderSimple size={20} weight="fill" className="text-amber-500" /></div>
                <span className="font-semibold text-stone-800 text-sm truncate flex-1 text-left">{f.title}</span>
                <CaretRight size={16} className="text-stone-300 shrink-0" />
              </button>
            ))}
            {items.map((it, i) => {
              const m = it.status ? STATUS_META[it.status] : null;
              return (
                <motion.div key={it.key} data-testid={`haf-item-${tab}-${it.key}`}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                  className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <p className="font-semibold text-stone-800 text-sm flex-1">{it.label || it.arabic}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      {it.audio && (
                        <button data-testid={`haf-play-${it.key}`} onClick={() => toggle(it)}
                          className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center active:scale-90 transition-transform">
                          {playing === it.key ? <Pause size={15} weight="fill" /> : <Play size={15} weight="fill" />}
                        </button>
                      )}
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${m ? m.cls : "bg-stone-100 text-stone-400"}`}>
                        {m ? <m.icon size={12} weight="fill" /> : <Circle size={12} />} {m ? m.label : "Belum dinilai"}
                      </span>
                    </div>
                  </div>
                  {it.arabic && <p className="font-arabic text-2xl text-right text-stone-800 leading-loose">{it.arabic}</p>}
                  {it.latin && <p className="text-primary/80 italic text-xs">{it.latin}</p>}
                  {it.meaning && <p className="text-stone-600 text-xs mt-0.5">{it.meaning}</p>}
                  {it.translation && <p className="text-stone-600 text-xs mt-0.5">{it.translation}</p>}
                  {it.example && <p className="text-stone-500 text-xs italic mt-0.5">"{it.example}"</p>}
                  {it.description && <p className="text-stone-600 text-xs mt-0.5">{it.description}</p>}
                  {it.note && <p className="text-stone-500 text-xs mt-1 bg-stone-50 rounded-lg p-2">{it.note}</p>}
                  <ReactionThread studentId={sid} category={tab} itemKey={it.key} itemLabel={it.label || it.arabic} thread={it.thread}
                    onPosted={() => api.get(`/progress?student_id=${sid}`).then((r) => setProgress(r.data))} />
                </motion.div>
              );
            })}
            {items.length === 0 && subFolders.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada materi pada folder ini.</p>}
          </div>
        )}
        <div className="h-6" />
      </div>
    </div>
  );
}
